import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  JsonApiResponseDto,
  Request,
  Response,
  TQueryParams,
  formatQueryParams,
  formatRequest,
  parseJsonApiResponse,
  prepareHeaders,
} from 'evolve-ui';
import {
  TOrganizationalEntity,
  TUserEntity,
} from 'evolve-ui/src/common/types/organizational-entity';
import { ContactGroupCategory } from './contact-groups.api';

/*

{
    "data": {
        "address": {
            "id": 3666,
            "postalCode": "4150-228 PORTO",
            "locationId": 5760
        },
        "customer": {
            "id": 4,
            "name": "FERREIRA - CONSTRUÇÃO, S.A. (GRUPO GFH)",
            "number": "1368",
            "customerStatusId": 3,
            "caCodeId": 431,
            "caCode": {
                "id": 431,
                "name": "Construção de edifícios (residenciais e não residenciais).",
                "activitySectorId": 6,
                "activitySector": {
                    "id": 6,
                    "name": "Construção"
                }
            }
        }
    },
    "messages": [],
    "relationshipNames": [
        "messages"
    ]
}

*/
// * Contact Type
export type TCampaignContact = {
  id?: string;
  type?: string;
  contactId?: number;
  name?: string;
  phone?: string;
  email?: string;
  // ! FIX ME
  data?: {
    anonymized?: boolean;
    address?: {
      id?: number;
      postalCode?: string;
      locationId?: number;
    };
    customer?: {
      id?: number;
      name?: string;
      number?: string;
      customerStatusId?: number;
      caCodeId?: number;
      caCode?: {
        id?: number;
        name?: string;
        activitySectorId?: number;
        activitySector?: {
          id?: number;
          name?: string;
        };
      };
    };
  };
  state?: string;
  messages?: any[];
};

// * Template Type
type TCampaignTemplate = {
  id?: string;
  name?: string;
  data?: {
    editable?: boolean;
    profile?: string;
    templateId?: string;
  };
};

// * Campaign Status.
// * Currently we have DRAFT.... campaign status.
export enum CampaignState {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  ERROR = 'error',
}

export const CampaignStatePT = {
  [CampaignState.DRAFT]: 'rascunho',
  [CampaignState.SCHEDULED]: 'agendada',
  [CampaignState.PROCESSING]: 'a enviar',
  [CampaignState.PROCESSED]: 'processada',
  [CampaignState.ERROR]: 'erro',
};

export enum CampaignContactState {
  PROCESSED = 'processed',
  DEFERRED = 'deferred',
  BOUNCE = 'bounce',
  DROPPED = 'dropped',
  DELIVERED = 'delivered',
  OPEN = 'open',
  CLICK = 'click',
  SPAMREPORT = 'spamreport',
  UNSUBSCRIBE = 'unsubscribe',
  GROUP_UNSUBSCRIBE = 'group_unsubscribe',
  GROUP_RESUBSCRIBE = 'group_resubscribe',
  SUBMITTED = 'submitted',
  // TWILIO
  FAILED = 'failed',
  SENT = 'sent',
  COMUNCATION_NOT_ACCEPTED = 'comunication_not_accepted',
  ANONYMIZED = 'anonymized_contact',
}

export const CampaignContactStatePT: any = {
  [CampaignContactState.PROCESSED]: 'processada',
  [CampaignContactState.DEFERRED]: 'a enviar',
  [CampaignContactState.BOUNCE]: 'erro',
  [CampaignContactState.DROPPED]: 'erro',
  [CampaignContactState.DELIVERED]: 'entregue',
  [CampaignContactState.OPEN]: 'aberto',
  [CampaignContactState.CLICK]: 'clique',
  [CampaignContactState.SPAMREPORT]: 'spam',
  [CampaignContactState.UNSUBSCRIBE]: 'unsub',
  [CampaignContactState.GROUP_UNSUBSCRIBE]: 'unsub grupo',
  [CampaignContactState.GROUP_RESUBSCRIBE]: 'resub grupo',
  [CampaignContactState.SUBMITTED]: 'submetida',
  [CampaignContactState.COMUNCATION_NOT_ACCEPTED]: 'não enviado',
  [CampaignContactState.FAILED]: 'erro',
  [CampaignContactState.SENT]: 'enviada',
  [CampaignContactState.ANONYMIZED]: 'anonimizado',
};

export enum CampaignCategory {
  CUSTOMERS = 'customers',
  EMPLOYEES = 'employees',
}

export enum CampaignChannel {
  SMS = 'sms',
  EMAIL = 'email',
}

// * Campaign Send Type.
// * Currently we have NOW and SCHEDULED campaign send types.
export enum CampaignSendType {
  NOW = 'now',
  SCHEDULED = '',
  DELAY = 'delay',
}

// * Campaign Contact Type.
export enum CampaignContactSelectionType {
  CONTACT_GROUP = 'contactGroup',
  DIRECT = 'direct',
}

export type TCampaign = {
  id?: string;
  type?: 'campaign';
  from?: string;
  name?: string;
  channel?: CampaignChannel;
  category?: CampaignCategory;
  agency?: TOrganizationalEntity;
  createdByAgency?: TOrganizationalEntity;
  contactGroup?: { id?: string };
  directContacts?: number[];
  template?: TCampaignTemplate;
  templateContent?: string;
  creator?: TUserEntity;
  relationshipNames?: string[];
  scheduled?: 'now' | '' | number;
  sentTimestamp?: number;
  state?: CampaignState;
  anonymized?: boolean;

  // ! FIX ME
  messages?: any;
  createdAt?: number;
  updatedAt?: number;
  contacts: TCampaignContact[];
};

export const campaignsApi = createApi({
  reducerPath: 'campaigns',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_CAMPAIGNS_API_URL}`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['Campaign'],
  endpoints: (builder) => {
    return {
      // * GET
      getCampaigns: builder.query<Response<TCampaign>, TQueryParams>({
        providesTags: ['Campaign'],
        query: formatQueryParams,
        transformResponse: (res, meta, args) => {
          return parseJsonApiResponse<TCampaign>(
            res as JsonApiResponseDto<TCampaign>,
            meta,
            args,
            true,
          );
        },
      }),

      getSingleCampaign: builder.query<
        Response<TCampaign>,
        TQueryParams & { campaignId: string }
      >({
        providesTags: ['Campaign'],
        query: ({ campaignId, populate }) =>
          formatQueryParams({ populate }, campaignId),
        transformResponse: (response, meta, args) =>
          parseJsonApiResponse<TCampaign>(
            response as JsonApiResponseDto<TCampaign>,
            meta,
            args,
            true,
          ),
      }),

      getCampaignContacts: builder.query<
        Response<TCampaignContact>,
        TQueryParams & { campaignId: string }
      >({
        providesTags: ['Campaign'],
        query: ({ campaignId, ...rest }) =>
          formatQueryParams(rest, `${campaignId}/contacts`),
        transformResponse: (response, meta, args) =>
          parseJsonApiResponse<TCampaignContact>(
            response as JsonApiResponseDto<TCampaignContact>,
            meta,
            args,
            true,
          ),
      }),

      // * POST
      postCampaign: builder.mutation<
        Response<TCampaign>,
        Omit<TCampaign, 'id' | 'state' | 'contacts'>
      >({
        invalidatesTags: ['Campaign'],
        query: (params) =>
          formatRequest({ ...params, type: 'campaign' }, Request.POST, '', [
            'template',
            'contactGroups',
          ]),
        transformResponse: parseJsonApiResponse<TCampaign>,
      }),

      postCampaignContacts: builder.mutation<
        Response<TCampaign>,
        {
          campaignId: string;
          contactIds: string[];
          type: ContactGroupCategory;
        }
      >({
        invalidatesTags: ['Campaign'],
        query: ({ campaignId, contactIds, type }) => {
          return {
            url: `${campaignId}/contacts?type=${type}`,
            body: {
              data: contactIds.map((id) => {
                return { attributes: { contactId: id }, type: 'contacts' };
              }),
            },
            method: Request.POST,
          };
        },
        transformResponse: parseJsonApiResponse<TCampaign>,
      }),

      duplicateCampaign: builder.mutation<
        Response<TCampaign>,
        { campaignId: string }
      >({
        query: ({ campaignId }) =>
          formatRequest(
            { type: 'campaign' },
            Request.POST,
            `${campaignId}/duplicate`,
          ),
        transformResponse: parseJsonApiResponse<TCampaign>,
      }),

      resendCampaignContact: builder.mutation<
        Response<TCampaign>,
        { campaignId: string; contactId: string }
      >({
        query: ({ campaignId, contactId }) =>
          formatRequest(
            { type: 'campaign' },
            Request.POST,
            `${campaignId}/contacts/${contactId}/resend`,
          ),
        transformResponse: parseJsonApiResponse<TCampaign>,
      }),

      batchResendCampaignContact: builder.mutation<
        { successes: number; failures: number },
        { campaignId: string; contactIds: string[] }
      >({
        query: ({ campaignId, contactIds }) =>
          formatRequest(
            { type: 'resend-messages-batch', ids: contactIds },
            Request.POST,
            `${campaignId}/contacts/resend`,
          ),
      }),
      // * PATCH
      patchCampaign: builder.mutation<Response<TCampaign>, Partial<TCampaign>>({
        invalidatesTags: ['Campaign'],
        query: ({ id, ...params }) =>
          formatRequest({ ...params, type: 'campaign' }, Request.PATCH, id, [
            'template',
          ]),
        transformResponse: parseJsonApiResponse<TCampaign>,
      }),

      patchCampaignContact: builder.mutation<
        Response<TCampaignContact>,
        Partial<Pick<TCampaignContact, 'phone' | 'email'>> & {
          campaignId: string;
          contactId: string;
        }
      >({
        invalidatesTags: ['Campaign'],
        query: ({ campaignId, contactId, ...params }) =>
          formatRequest(
            { ...params, type: 'campaign' },
            Request.PATCH,
            `${campaignId}/contacts/${contactId}`,
          ),
        transformResponse: parseJsonApiResponse<TCampaignContact>,
      }),

      scheduleCampaign: builder.mutation<
        Response<any>,
        {
          campaignId: string;
          scheduled: 'now' | number;
        }
      >({
        invalidatesTags: ['Campaign'],
        query: ({ campaignId, ...params }) =>
          formatRequest(
            { ...params, type: 'campaign' },
            Request.PATCH,
            `${campaignId}/schedule`,
          ),
        transformResponse: parseJsonApiResponse<TCampaignContact>,
      }),

      cancelCampaign: builder.mutation<Response<any>, { campaignId: string }>({
        invalidatesTags: ['Campaign'],
        query: ({ campaignId }) =>
          formatRequest(
            { type: 'campaign' },
            Request.PATCH,
            `${campaignId}/cancel`,
          ),
        transformResponse: parseJsonApiResponse<TCampaignContact>,
      }),

      // * DELETE
      deleteCampaign: builder.mutation<Response<TCampaign>, { id: string }>({
        invalidatesTags: ['Campaign'],
        query: ({ id, ...params }) =>
          formatRequest({ ...params, type: 'campaign' }, Request.DELETE, id),
        transformResponse: parseJsonApiResponse<TCampaign>,
      }),

      deleteCampaignContacts: builder.mutation<
        Response<TCampaignContact>,
        { campaignId: string; contactIds: string[] }
      >({
        query: ({ campaignId, contactIds }) => {
          return {
            url: `${campaignId}/contacts`,
            body: {
              data: contactIds.map((id) => {
                return { id, type: 'campaign' };
              }),
            },
            method: Request.DELETE,
          };
        },
        transformResponse: parseJsonApiResponse<TCampaignContact>,
      }),
    };
  },
});

export const {
  // * GET
  useGetCampaignsQuery,
  useGetSingleCampaignQuery,
  useGetCampaignContactsQuery,
  // * POST
  usePostCampaignMutation,
  useDuplicateCampaignMutation,
  usePostCampaignContactsMutation,
  useResendCampaignContactMutation,
  useBatchResendCampaignContactMutation,
  // * PATCH
  usePatchCampaignMutation,
  usePatchCampaignContactMutation,
  useScheduleCampaignMutation,
  useCancelCampaignMutation,
  // * DELETE
  useDeleteCampaignMutation,
  useDeleteCampaignContactsMutation,
} = campaignsApi;
